import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';

import type {
	useContentByLabelDataQuery$data,
	useContentByLabelDataQuery$variables,
} from './__generated__/useContentByLabelDataQuery.graphql.ts';

interface UseContentByLabelDataResult {
	data: useContentByLabelDataQuery$data | undefined;
	error: Error | null;
	loading: boolean;
}

/**
 * Hook that fetches content matching the given CQL query via the
 * confluence_search GraphQL endpoint.
 *
 * Accepts pre-built query variables from useParsedMacroParams (via
 * macroParamsForQuery), avoiding any reprocessing of macro params here.
 */
export const useContentByLabelData = (
	macroParamsForQuery: useContentByLabelDataQuery$variables,
): UseContentByLabelDataResult => {
	const { cql } = macroParamsForQuery;
	const shouldSkipQuery = !cql?.trim();
	const { data, error, loading } = useQueryInterop<
		useContentByLabelDataQuery$data,
		useContentByLabelDataQuery$variables
	>(
		graphql`
			query useContentByLabelDataQuery(
				$cql: String!
				$first: Int
				$includeExcerpt: Boolean!
				$fallbackToBody: Boolean!
				$maxExcerptBlocks: Int
				$skipSearchTitle: Boolean = false
				$skipContentTitle: Boolean = true
			) {
				search: confluence_search(cql: $cql, first: $first) {
					nodes {
						title @skip(if: $skipSearchTitle)
						url
						lastModified
						content {
							id
							title @skip(if: $skipContentTitle)
							type
							subType
							macroExcerpt @include(if: $includeExcerpt) {
								atlasDocFormat(fallbackToBody: $fallbackToBody, maxBlocks: $maxExcerptBlocks)
							}
							body @include(if: $includeExcerpt) {
								atlas_doc_format {
									mediaToken {
										token
									}
								}
							}
							space {
								id
								key
								name
							}
							labels {
								nodes {
									id
									name
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				...macroParamsForQuery,
				skipSearchTitle: fg('cc_rewrite_macros_use_content_not_search_title'),
				skipContentTitle: !fg('cc_rewrite_macros_use_content_not_search_title'),
			},
			skip: shouldSkipQuery,
			fetchPolicy: 'cache-first',
		},
	);

	return {
		data,
		error: error ?? null,
		loading: Boolean(loading),
	};
};
